import { useRef, useState } from 'react'
import { useUser } from '../contexts/UserContext'
import { submitVolunteerIdPhoto } from '../api/volunteerVerification'
import { isVolunteerIdApproved, volunteerIdRestrictionMessage } from '../utils/volunteerAccess'

type Props = {
  className?: string
}

const VolunteerIdStatusBanner = ({ className = '' }: Props) => {
  const { user, refreshUser } = useUser()
  const fileRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  if (!user || user.userType !== 'VOLUNTEER' || isVolunteerIdApproved(user)) {
    return null
  }

  const status = user.volunteerIdVerification?.status
  const canReupload = status === 'REJECTED' || !status

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Choose a photo of your driver’s license or permit first.')
      return
    }
    setUploading(true)
    setError(null)
    setSuccess(null)
    try {
      await submitVolunteerIdPhoto(selectedFile)
      setSelectedFile(null)
      if (fileRef.current) fileRef.current.value = ''
      await refreshUser()
      setSuccess('Photo ID submitted. An admin will review it shortly.')
    } catch (e: any) {
      setError(e?.message || 'Failed to upload photo ID. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className={`bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-950 ${className}`}>
      <p className="font-medium">Photo ID approval required</p>
      <p className="mt-1">{volunteerIdRestrictionMessage(user)}</p>

      {canReupload && (
        <div className="mt-4 space-y-3">
          <p className="text-amber-900">
            To continue, upload a clear photo of your driver’s license or permit (JPG or PNG, max 5MB).
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                setSelectedFile(e.target.files?.[0] || null)
                setError(null)
                setSuccess(null)
              }}
              className="block w-full text-sm text-amber-950 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-amber-100 file:text-amber-950 file:font-medium hover:file:bg-amber-200"
            />
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
              className="shrink-0 px-4 py-2 rounded-md bg-amber-700 text-white text-sm font-medium hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading…' : 'Re-upload photo ID'}
            </button>
          </div>
          {selectedFile && (
            <p className="text-xs text-amber-800">Selected: {selectedFile.name}</p>
          )}
        </div>
      )}

      {status === 'PENDING' && (
        <p className="mt-3 text-amber-900">
          No action needed right now — we’ll email you when your ID is approved.
        </p>
      )}

      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      {success && <p className="mt-3 text-sm text-green-700">{success}</p>}
    </div>
  )
}

export default VolunteerIdStatusBanner
